import { Fragment, ReactNode, useState } from 'react';
import {
  DataTable,
  DataTableRenderProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableExpandedRow,
  TableExpandHeader,
  TableExpandRow,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
} from '@carbon/react';
import { FormattedCount } from 'components/formatted-count';
import type { AuditRowLike } from './types';
import { formatDate } from './formatters';
import style from './audit-log.module.scss';

const HEADERS = [
  { key: 'sequence', header: 'Sequence' },
  { key: 'dataCategory', header: 'Data Category' },
  { key: 'date', header: 'Date' },
  { key: 'user', header: 'User' },
  { key: 'action', header: 'Action' },
  { key: 'summary', header: 'Summary' },
];

interface DisplayRow {
  id: string;
  sequence: number;
  dataCategory: string;
  date: string;
  user: string;
  action: string;
  summary: string;
}

// Maps the section token parsed off an event `kind` to its display label.
// AFE events are `afe_structuring.{section}_…` (well/match/imported/suggestions
// all belong to the Well section); DO events are
// `division_order_structuring.{overview|interest|well}_…`; Parties events are
// `structured_{party|address}.…`.
const SECTION_LABEL: Record<string, string> = {
  identification: 'Identification',
  overview: 'Overview',
  interest: 'Interest',
  // RC line-item events are `royalty_check_structuring.line_item_…`; the regex
  // below captures up to the first underscore, so the token is `line`.
  line: 'Line Item',
  well: 'Well',
  match: 'Well',
  imported: 'Well',
  suggestions: 'Well',
  cost: 'Cost',
  party: 'Party',
  address: 'Address',
  // Related Items events are `related_item.{linked|edited|removed|downloaded}`
  // — the whole prefix is the category, keyed here for the classifier below.
  related_item: 'Related Items',
};

// "Data Category" classifies each row by section. Agent runs are their own
// category; events derive it from the `kind` prefix. Unknown shapes fall back to
// an em dash so the column never throws on an unexpected feed.
function dataCategoryFor(row: AuditRowLike): string {
  if (row.activityType === 'agent_run') return 'Agent Run';
  const kind = (row.data as { kind?: string }).kind ?? '';
  // Related Items events carry the whole `related_item` prefix as the
  // category (the suffix is the action, not a section), so classify them first.
  if (/^related_item[._]/.test(kind)) return SECTION_LABEL.related_item;
  const token = kind.match(
    /^(?:afe_structuring|division_order_structuring|royalty_check_structuring|structured)[._]([a-z]+)/
  )?.[1];
  if (!token) return '—';
  return SECTION_LABEL[token] ?? token.charAt(0).toUpperCase() + token.slice(1);
}

interface Props<T extends AuditRowLike> {
  rows: T[];
  /** The feed's full size when only some of it is loaded. Defaults to the rows shown. */
  totalCount?: number;
  /** Expanded body for a given source row (agent-specific). */
  renderExpanded: (row: T) => ReactNode;
  /** Show the "Activity Log — N events" heading. Defaults to true. */
  showHeading?: boolean;
  /** Message rendered when there are no rows. */
  emptyMessage?: string;
}

// Shared shell for an agent activity log: an "Activity Log" heading, a Carbon
// DataTable with a search toolbar and sortable Sequence / Data Category / Date /
// User / Action / Summary columns, and per-row expansion delegated to
// `renderExpanded`. Used by both the Entity Match Agent (Parties) and Well Match
// Agent (AFE wells) logs so the two stay in lockstep.
export function AuditLogTable<T extends AuditRowLike>({
  rows,
  totalCount,
  renderExpanded,
  showHeading = true,
  emptyMessage = 'No activity log entries recorded.',
}: Props<T>) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const rowIndex = new Map(rows.map((r) => [r.activityId, r]));

  const displayRows: DisplayRow[] = rows.map((r, index) => ({
    id: r.activityId,
    // 1-based position within the loaded feed (newest-first), like the
    // pre-drill agent log. Travels with the row, so it stays meaningful after
    // the user sorts by another column.
    sequence: index + 1,
    dataCategory: dataCategoryFor(r),
    date: formatDate(r.createdAt),
    user: r.data.user,
    action: r.data.action,
    summary: r.data.description,
  }));

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const eventCount = Math.max(totalCount ?? 0, rows.length);
  // Search and sort run over the loaded rows only.
  const partial = rows.length < eventCount;
  const heading = showHeading && (
    <p className={style.heading}>
      Activity Log{' '}
      <span className={style.count}>
        <FormattedCount value={eventCount} />{' '}
        {eventCount === 1 ? 'event' : 'events'}
        {partial && (
          <>
            {' · '}
            <FormattedCount value={rows.length} /> loaded
          </>
        )}
      </span>
    </p>
  );

  if (rows.length === 0) {
    return (
      <>
        {showHeading && (
          <p className={style.heading}>
            Activity Log <span className={style.count}>0 events</span>
          </p>
        )}
        <p className={style.empty}>{emptyMessage}</p>
      </>
    );
  }

  return (
    <>
      {heading}
      {partial && (
        <p className={style.partialNote}>
          Search and sort cover the loaded events. Load more to include older
          ones.
        </p>
      )}
      <div className={style.table}>
        <DataTable rows={displayRows} headers={HEADERS} isSortable>
          {({
            rows: dtRows,
            headers,
            getHeaderProps,
            getTableProps,
            onInputChange,
          }: DataTableRenderProps<DisplayRow, typeof HEADERS>) => (
            <TableContainer>
              <TableToolbar size="sm">
                <TableToolbarContent>
                  <TableToolbarSearch
                    placeholder="Search activity log…"
                    persistent
                    size="sm"
                    onChange={onInputChange}
                  />
                </TableToolbarContent>
              </TableToolbar>
              <Table {...getTableProps()} size="sm">
                <TableHead>
                  <TableRow>
                    <TableExpandHeader aria-label="expand row" />
                    {headers.map((h) => (
                      <TableHeader
                        {...getHeaderProps({ header: h })}
                        key={h.key}
                      >
                        {h.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dtRows.map((row) => {
                    const source = rowIndex.get(row.id);
                    if (!source) return null;
                    const isExpanded = expanded.has(row.id);
                    return (
                      <Fragment key={row.id}>
                        <TableExpandRow
                          isExpanded={isExpanded}
                          onExpand={() => toggle(row.id)}
                          aria-label={`Toggle ${row.id} details`}
                        >
                          {row.cells.map((cell) => (
                            <TableCell key={cell.id}>
                              {cell.value as unknown as ReactNode}
                            </TableCell>
                          ))}
                        </TableExpandRow>
                        {isExpanded && (
                          <TableExpandedRow colSpan={HEADERS.length + 1}>
                            {renderExpanded(source)}
                          </TableExpandedRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DataTable>
      </div>
    </>
  );
}
